import React, { useEffect, useState } from "react";
import { Play } from "../../api/types";
import { AuthExpiredError } from "../../api/auth";
import { playToSvg } from "../../render/playToSvg";
import { svgToPng } from "../../render/svgToPng";
import { renderPlayPng } from "../../render/renderPlayPng";
import { formatInsertError } from "../office/insertErrors";
import { insertImageIntoBox } from "../office/insertImage";

interface Props {
  play: Play;
  selected: boolean;
  onToggleSelect: () => void;
  onAuthExpired: () => void;
}

export default function PlayCard({ play, selected, onToggleSelect, onAuthExpired }: Props) {
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [insertError, setInsertError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setThumbnail(null);
    setThumbError(false);

    playToSvg(play)
      .then(({ svg, fieldDataUri, width, height }) => svgToPng(svg, width, height, 0.25, fieldDataUri))
      .then((png) => {
        if (!cancelled) {
          setThumbnail(png);
        }
      })
      .catch((err) => {
        console.error("[CHLK thumbnail error]", play.name, err);
        if (!cancelled) {
          setThumbError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [play]);

  async function handleInsert() {
    setInserting(true);
    setInsertError(null);
    try {
      const { png, width, height } = await renderPlayPng(play);
      await insertImageIntoBox(png, width, height, play._id);
    } catch (err) {
      console.error("[CHLK insert error]", err);
      if (err instanceof AuthExpiredError) {
        onAuthExpired();
      } else {
        setInsertError(formatInsertError(err));
      }
    } finally {
      setInserting(false);
    }
  }

  return (
    <div className={`play-card${selected ? " selected" : ""}`}>
      <label className="play-card-select">
        <input type="checkbox" checked={selected} onChange={onToggleSelect} />
      </label>
      <div className="play-card-thumb">
        {thumbnail ? (
          <img src={`data:image/png;base64,${thumbnail}`} alt={play.name} />
        ) : thumbError ? (
          <div className="thumb-error">Preview unavailable</div>
        ) : (
          <div className="thumb-placeholder" />
        )}
      </div>
      <div className="play-card-name">{play.name}</div>
      {insertError && <div className="play-card-error">{insertError}</div>}
      <button className="btn-insert" onClick={handleInsert} disabled={inserting}>
        {inserting ? "Inserting…" : "Insert"}
      </button>
    </div>
  );
}
